import { cn } from '@sohanemon/utils';
import { Iconify } from '@sohanemon/utils/components';
import { terminal_loading } from '@sohanscript/i18n';
import { TerminalFrame } from '@sohanscript/ui/terminal';
import {
	type DOMMotionComponents,
	domAnimation,
	type HTMLMotionProps,
	LazyMotion,
	type MotionProps,
	useInView,
} from 'motion/react';
import * as m from 'motion/react-m';
import {
	Children,
	type ComponentType,
	createContext,
	type RefAttributes,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import { Link } from '../link';

interface TerminalContextValue {
	duration: number;
	completeItem: (index: number) => void;
	activeIndex: number;
	sequenceStarted: boolean;
	itemIndex: number | null;
}

const TerminalContext = createContext<TerminalContextValue | null>(null);

const useTerminal = () => useContext(TerminalContext);

const motionElements = {
	article: m.article,
	div: m.div,
	h1: m.h1,
	h2: m.h2,
	h3: m.h3,
	h4: m.h4,
	h5: m.h5,
	h6: m.h6,
	li: m.li,
	p: m.p,
	section: m.section,
	span: m.span,
} as const;

type MotionElementType = Extract<
	keyof DOMMotionComponents,
	keyof typeof motionElements
>;
type TerminalTypingMotionComponent = ComponentType<
	Omit<HTMLMotionProps<'span'>, 'ref'> & RefAttributes<HTMLElement>
>;

interface AnimatedSpanProps extends MotionProps {
	children: React.ReactNode;
	delay?: number;
	className?: string;
	startOnView?: boolean;
	holdSequence?: boolean;
}

export const AnimatedSpan = ({
	children,
	delay = 0,
	className,
	startOnView = false,
	holdSequence = false,

	...props
}: AnimatedSpanProps) => {
	const elementRef = useRef<HTMLDivElement | null>(null);
	const isInView = useInView(elementRef as React.RefObject<Element>, {
		amount: 0.3,
		once: true,
	});

	const terminal = useTerminal();
	const itemIndex = terminal?.itemIndex ?? null;
	const [hasStarted, setHasStarted] = useState(false);
	useEffect(() => {
		if (!terminal || itemIndex === null) return;
		if (!terminal.sequenceStarted) return;
		if (hasStarted) return;
		if (terminal.activeIndex === itemIndex) {
			setHasStarted(true);
		}
	}, [terminal, hasStarted, itemIndex]);

	const shouldAnimate = terminal ? hasStarted : startOnView ? isInView : true;

	return (
		<m.div
			ref={elementRef}
			initial={{ opacity: 0, y: -5 }}
			animate={shouldAnimate ? { opacity: 1, y: 0 } : { opacity: 0, y: -5 }}
			transition={{
				duration: terminal?.duration ?? 0.3,
				delay: terminal ? 0 : delay / 1000,
			}}
			className={cn('font-mono text-foreground text-xs', className)}
			onAnimationComplete={(definition) => {
				if (!terminal) return;
				if (itemIndex === null) return;
				if (holdSequence) return;
				if ((definition as any).opacity === 0) return;
				terminal.completeItem(itemIndex);
			}}
			{...props}
		>
			{children}
		</m.div>
	);
};

export const TerminalSuccess = ({
	children,
}: {
	children: React.ReactNode;
}) => {
	return (
		<AnimatedSpan className="text-success">
			<Iconify icon="lucide:check" className="mr-2 inline-block" />
			{children}
		</AnimatedSpan>
	);
};

export const TerminalInfo = ({ children }: { children: React.ReactNode }) => {
	return (
		<AnimatedSpan className="text-primary/80">
			<Iconify icon="lucide:info" className="mr-2 inline-block" />
			{children}
		</AnimatedSpan>
	);
};

type TerminalLinkProps = React.ComponentProps<typeof Link> & {
	label: string;
	description?: string;
};

export const TerminalLink = ({
	label,
	description,
	className,
	...props
}: TerminalLinkProps) => {
	return (
		<AnimatedSpan
			className={cn(
				'inline-flex items-center gap-2 ps-2 transition-colors',
				className,
			)}
		>
			<Link className="group contents lowercase" {...props}>
				<Iconify
					icon="lucide:arrow-right"
					className="shrink-0 text-primary/70"
				/>
				<span className="font-medium text-primary underline-offset-2 transition-colors group-hover:text-primary group-hover:underline">
					{label}
				</span>
			</Link>
			{description && (
				<span className="text-muted-foreground">{description}</span>
			)}
		</AnimatedSpan>
	);
};

export const TerminalPrompt = ({ className }: { className?: string }) => {
	const terminal = useTerminal();
	const itemIndex = terminal?.itemIndex ?? null;

	useEffect(() => {
		if (!terminal || itemIndex === null) return;
		if (terminal.activeIndex === itemIndex) {
			terminal.completeItem(itemIndex);
		}
	}, [terminal, itemIndex]);

	return (
		<AnimatedSpan className={cn('flex items-center gap-2 pt-1', className)}>
			<span className="text-primary/50">~</span>
			<m.span
				className="inline-block h-2.5 w-1.5 rounded-[1px] bg-primary/70"
				animate={{ opacity: [1, 1, 0, 0] }}
				transition={{
					duration: 1,
					repeat: Infinity,
					ease: 'linear',
					times: [0, 0.5, 0.5, 1],
				}}
			/>
		</AnimatedSpan>
	);
};

type TerminalProgressBarProps = {
	label?: string;
	fillStarted?: boolean;
	duration?: number;
} & Omit<AnimatedSpanProps, 'children'>;

function easeOutExpo(t: number) {
	return t === 1 ? 1 : 1 - 2 ** (-10 * t);
}

const BLOCK_W = 3;
const BLOCK_H = 10;
const GAP = 2;

export function TerminalProgressBar({
	className,
	label = terminal_loading(),
	duration = 1,
	startOnView = false,
	...props
}: TerminalProgressBarProps) {
	const [progress, setProgress] = useState(0);
	const [total, setTotal] = useState<number | null>(null);
	const trackRef = useRef<HTMLDivElement>(null);
	const rafRef = useRef<number>(0);
	const terminal = useTerminal();
	const itemIndex = terminal?.itemIndex ?? null;
	const hasSequenceTurn = terminal
		? terminal.activeIndex === itemIndex && terminal.sequenceStarted
		: true;
	const isInView = useInView(trackRef as React.RefObject<Element>, {
		amount: 0.3,
		once: true,
	});
	const canFill = terminal ? hasSequenceTurn : startOnView ? isInView : true;

	useEffect(() => {
		const el = trackRef.current;
		if (!el) return;

		const measure = () => {
			if (!trackRef.current) return;
			const w = trackRef.current.getBoundingClientRect().width;
			setTotal(Math.max(1, Math.floor((w + GAP) / (BLOCK_W + GAP))));
		};

		measure();
		const ro = new ResizeObserver(measure);
		ro.observe(el);
		return () => ro.disconnect();
	}, []);

	useEffect(() => {
		if (!canFill) return;
		const totalMs = duration * 2000;
		const start = performance.now();

		const tick = (now: number) => {
			const t = Math.min((now - start) / totalMs, 1);
			setProgress(easeOutExpo(t));
			if (t < 1) rafRef.current = requestAnimationFrame(tick);
		};

		rafRef.current = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(rafRef.current);
	}, [duration, canFill]);

	const filled = total !== null ? Math.round(progress * total) : 0;
	const pct = Math.round(progress * 100);
	const done = pct === 100;
	useEffect(() => {
		if (!done || !terminal || itemIndex === null) return;
		terminal.completeItem(itemIndex);
	}, [done, terminal, itemIndex]);

	return (
		<AnimatedSpan
			className={cn('flex flex-col gap-1.25', className)}
			style={{ fontFamily: 'var(--font-mono)' }}
			holdSequence
			{...props}
		>
			{/* top row */}
			<div className="flex items-center justify-between text-2xs text-muted-foreground uppercase tracking-widest">
				<span className="flex items-center gap-1.5">
					<span
						className={cn(
							'size-1.25 shrink-0 rounded-full',
							done ? 'bg-success' : 'bg-muted-foreground',
						)}
					/>
					{label}
				</span>
				<span className="tabular-nums">{pct}%</span>
			</div>

			{/* track */}
			<div
				ref={trackRef}
				className="flex w-full items-end"
				style={{ gap: GAP, height: BLOCK_H }}
			>
				{total !== null &&
					Array.from({ length: total }, (_, i) => {
						const isFilled = i < filled;
						const isCursor = i === filled && !done;
						return (
							<div
								key={i}
								style={{
									width: BLOCK_W,
									flexShrink: 0,
									height: isCursor
										? BLOCK_H
										: isFilled
											? BLOCK_H
											: BLOCK_H * 0.4,
									alignSelf: 'flex-end',
									borderRadius: 1,
									opacity: isFilled ? 1 : isCursor ? 1 : 0.2,
									animation: isCursor ? 'blink 0.9s step-end infinite' : 'none',
									background: isFilled
										? 'var(--color-secondary)'
										: isCursor
											? 'var(--color-primary)'
											: 'var(--color-card)',
									transition: isFilled ? 'none' : undefined,
								}}
							/>
						);
					})}
			</div>

			{/* bottom row */}
			<div
				className="flex justify-between text-2xs text-muted-foreground tracking-[0.06em] opacity-60"
				style={{ fontFamily: 'var(--font-mono)' }}
			>
				<span>{done ? '■ complete' : '○ waiting'}</span>
				<span>
					{total !== null ? `${filled} / ${total} sectors` : 'measuring...'}
				</span>
			</div>
			<style>
				{`
@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}
`}
			</style>
		</AnimatedSpan>
	);
}

interface TypingAnimationProps extends Omit<MotionProps, 'children'> {
	children: string;
	className?: string;
	duration?: number;
	delay?: number;
	as?: MotionElementType;
	startOnView?: boolean;
}

export const TypingAnimation = ({
	children,
	className,
	duration = 60,
	delay = 0,
	as: Component = 'span',
	startOnView = true,
	...props
}: TypingAnimationProps) => {
	if (typeof children !== 'string') {
		throw new Error('TypingAnimation: children must be a string. Received:');
	}

	const MotionComponent = motionElements[
		Component
	] as TerminalTypingMotionComponent;

	const [displayedText, setDisplayedText] = useState<string>('');
	const [started, setStarted] = useState(false);
	const elementRef = useRef<HTMLElement | null>(null);
	const isInView = useInView(elementRef as React.RefObject<Element>, {
		amount: 0.3,
		once: true,
	});

	const terminal = useTerminal();
	const itemIndex = terminal?.itemIndex ?? null;
	const hasSequence = terminal !== null;
	const sequenceStarted = terminal?.sequenceStarted ?? false;
	const sequenceActiveIndex = terminal?.activeIndex ?? null;
	const sequenceCompleteItemRef = useRef<
		TerminalContextValue['completeItem'] | null
	>(null);
	const sequenceItemIndexRef = useRef<number | null>(null);

	useEffect(() => {
		sequenceCompleteItemRef.current = terminal?.completeItem ?? null;
		sequenceItemIndexRef.current = itemIndex;
	}, [terminal?.completeItem, itemIndex]);

	useEffect(() => {
		let startTimeout: ReturnType<typeof setTimeout> | null = null;

		if (hasSequence && itemIndex !== null) {
			if (sequenceStarted && !started && sequenceActiveIndex === itemIndex) {
				setStarted(true);
			}
		} else if (!startOnView || isInView) {
			startTimeout = setTimeout(() => setStarted(true), delay);
		}

		return () => {
			if (startTimeout !== null) {
				clearTimeout(startTimeout);
			}
		};
	}, [
		delay,
		startOnView,
		isInView,
		started,
		hasSequence,
		sequenceActiveIndex,
		sequenceStarted,
		itemIndex,
	]);

	useEffect(() => {
		let typingEffect: ReturnType<typeof setInterval> | null = null;

		if (started) {
			let i = 0;
			typingEffect = setInterval(() => {
				if (i < children.length) {
					setDisplayedText(children.substring(0, i + 1));
					i++;
				} else {
					if (typingEffect !== null) {
						clearInterval(typingEffect);
					}
					const completeItem = sequenceCompleteItemRef.current;
					const currentItemIndex = sequenceItemIndexRef.current;
					if (completeItem && currentItemIndex !== null) {
						completeItem(currentItemIndex);
					}
				}
			}, duration);
		}

		return () => {
			if (typingEffect !== null) {
				clearInterval(typingEffect);
			}
		};
	}, [children, duration, started]);

	return (
		<MotionComponent
			ref={elementRef}
			className={cn('font-mono text-foreground text-xs', className)}
			{...props}
		>
			{displayedText}
		</MotionComponent>
	);
};

type TerminalProps = Partial<TerminalContextValue> & {
	children: React.ReactNode;
	className?: string;
	sequence?: boolean;
	startOnView?: boolean;
	label?: string;
	/** Strips the card chrome (border, header, bg). Use inside a Window component. */
	bare?: boolean;
};

export const Terminal = ({
	children,
	className,
	label = '~/Code/sohanscript',
	sequence = true,
	startOnView = true,
	bare = false,
	duration = 0.3,
}: TerminalProps) => {
	const containerRef = useRef<HTMLDivElement | null>(null);
	const isInView = useInView(containerRef as React.RefObject<Element>, {
		amount: 0.3,
		once: true,
	});

	const [activeIndex, setActiveIndex] = useState(0);
	const sequenceHasStarted = sequence ? !startOnView || isInView : false;

	const contextValue = useMemo(() => {
		if (!sequence) return null;
		return {
			completeItem: (index: number) => {
				setActiveIndex((current) =>
					index === current ? current + 1 : current,
				);
			},
			activeIndex,
			sequenceStarted: sequenceHasStarted,
			duration,
		};
	}, [sequence, activeIndex, duration, sequenceHasStarted]);

	const childrenArray = useMemo(() => Children.toArray(children), [children]);
	const contextValues = useMemo(() => {
		if (!contextValue) return [];
		return childrenArray.map((_, itemIndex) => ({
			...contextValue,
			itemIndex,
		}));
	}, [childrenArray, contextValue]);

	const wrappedChildren = useMemo(() => {
		if (!sequence) return children;
		return contextValues.map((value) => (
			<TerminalContext.Provider key={value.itemIndex} value={value}>
				{childrenArray[value.itemIndex] as React.ReactNode}
			</TerminalContext.Provider>
		));
	}, [children, childrenArray, contextValues, sequence]);
	const content = (
		<LazyMotion features={domAnimation}>
			<TerminalFrame
				ref={containerRef}
				className={className}
				label={label}
				bare={bare}
			>
				{wrappedChildren}
			</TerminalFrame>
		</LazyMotion>
	);

	if (!sequence) return content;

	return content;
};
